import { computed, ref, shallowRef, watch, type Ref } from "vue";

import type {
  AiConversationSummary,
  Controller,
  ConversationId,
  OrgId,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import { releaseBestEffort } from "@/ref";

// Module-level snapshot of every conversation summary fetched this session.
// The conversation list pane is remounted when navigating between the Agent
// and AgentConversation routes, and a fresh mount would otherwise flash a
// second loading state while re-fetching data it just displayed: snapshots
// paint immediately, and the live refs replace them as they resolve.
const conversationSnapshots = new Map<ConversationId, AiConversationSummary>();

/**
 * Load conversation summaries for an agent from the controller RPC.
 * Each conversation ID is fetched via
 * controller.ai.agent.conversation.getSummary() — metadata only (id, title,
 * touched_last), never the history, which is too heavy to fetch for a whole
 * list. Hosts needing a conversation's content fetch that one conversation
 * in full with controller.ai.agent.conversation.get().
 */
export function useConversations(
  controller: Controller,
  organizationId: Ref<OrgId>,
  conversationIds: Ref<ConversationId[]>
) {
  // Track individual refs for each conversation ID
  const conversationRefs = shallowRef<
    Map<ConversationId, ReleasableRef<AiConversationSummary | null>>
  >(new Map());

  // True while some conversation has no data to show — neither a live ref
  // nor a session snapshot — and the first load pass hasn't committed yet:
  // lets hosts show a loading state instead of an empty list / "not found"
  // while the conversations are being fetched for the first time.
  const firstLoadDone = ref(false);
  const isLoading = computed(
    () =>
      !firstLoadDone.value &&
      conversationIds.value.some(
        (id) =>
          !conversationRefs.value.get(id)?.value &&
          !conversationSnapshots.has(id)
      )
  );

  // Watch conversationIds and load/release refs as needed.
  // We use watch (not watchEffect) with explicit cleanup via onInvalidate
  // so this works correctly even when called after awaits in async setup().
  const stopWatch = watch(
    conversationIds,
    async (newIds, _oldIds, onInvalidate) => {
      const oldRefs = new Map(conversationRefs.value);
      const newRefs = new Map<
        ConversationId,
        ReleasableRef<AiConversationSummary | null>
      >();

      // Only release refs created by an aborted in-flight run: once the run
      // commits, its refs live in conversationRefs and are released either by
      // a later run (when removed) or by release() below. Without this guard,
      // stopping the watcher would double-release the last run's fresh refs.
      let committed = false;
      onInvalidate(() => {
        if (committed) return;
        for (const [id, r] of newRefs) {
          if (!oldRefs.has(id)) releaseBestEffort(r);
        }
      });

      for (const id of newIds) {
        if (oldRefs.has(id)) {
          newRefs.set(id, oldRefs.get(id)!);
        } else {
          const r = await controller.ai.agent.conversation.getSummary(
            organizationId.value,
            id
          );
          newRefs.set(id, r);
        }
      }

      // Release refs that are no longer needed
      for (const [id, r] of oldRefs) {
        if (!newRefs.has(id)) releaseBestEffort(r);
      }

      for (const [id, r] of newRefs) {
        if (r.value) conversationSnapshots.set(id, r.value);
      }
      conversationRefs.value = newRefs;
      committed = true;
      firstLoadDone.value = true;
    },
    { immediate: true }
  );

  // Release every live conversation subscription and stop the watcher.
  // Idempotent.
  let released = false;
  function release() {
    if (released) return;
    released = true;
    stopWatch();
    for (const [id, r] of conversationRefs.value) {
      // Capture the freshest data for the next mount before releasing.
      if (r.value) conversationSnapshots.set(id, r.value);
      releaseBestEffort(r);
    }
    conversationRefs.value = new Map();
  }

  const conversations = computed<AiConversationSummary[]>(() => {
    const result: AiConversationSummary[] = [];
    for (const id of conversationIds.value) {
      const data =
        conversationRefs.value.get(id)?.value ?? conversationSnapshots.get(id);
      if (!data) continue;
      result.push(data);
    }
    return result;
  });

  return {
    conversations,
    isLoading,
    release,
  };
}
